import { z } from 'zod';

// ────────────────────────────────────────────────────────────────────────────
// Server-shaped rows (validated when consumed)
// ────────────────────────────────────────────────────────────────────────────

export const closureSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  startDate: z.string(), // YYYY-MM-DD
  endDate: z.string(),
  isActive: z.boolean(),
  apmcCount: z.number(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type Closure = z.infer<typeof closureSchema>;

const closureApmcSchema = z.object({
  id: z.string(),
  code: z.string(),
  name: z.string(),
  state: z.object({
    id: z.string(),
    code: z.string(),
    name: z.string(),
  }),
  district: z.object({
    id: z.string(),
    code: z.string(),
    name: z.string(),
  }),
});

const closureTranslationSchema = z.object({
  id: z.string(),
  localeId: z.string(),
  localeCode: z.string(),
  name: z.string(),
  description: z.string().nullable(),
});

// Detail omits apmcCount (caller has the full apmcs array — count it from there).
export const closureDetailSchema = closureSchema.omit({ apmcCount: true }).extend({
  apmcs: z.array(closureApmcSchema),
  translations: z.array(closureTranslationSchema),
});
export type ClosureDetail = z.infer<typeof closureDetailSchema>;
export type ClosureApmc = z.infer<typeof closureApmcSchema>;
export type ClosureTranslation = z.infer<typeof closureTranslationSchema>;

// ────────────────────────────────────────────────────────────────────────────
// Form schema
// ────────────────────────────────────────────────────────────────────────────

export const closureFormTranslationSchema = z.object({
  localeCode: z.string().min(1),
  localeName: z.string(), // display-only; stripped before submit
  name: z.string().max(200),
  description: z.string().max(2000),
});
export type ClosureFormTranslation = z.infer<
  typeof closureFormTranslationSchema
>;

const baseFields = {
  name: z.string().min(1, 'Name is required').max(200),
  description: z.string().max(2000).nullable(),
  startDate: z
    .string()
    .min(1, 'Start date is required')
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Use YYYY-MM-DD'),
  endDate: z
    .string()
    .min(1, 'End date is required')
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Use YYYY-MM-DD'),
  isActive: z.boolean(),
  // BigInt-safe over the wire as strings.
  apmcIds: z.array(z.string()).min(1, 'Select at least one APMC'),
  translations: z.array(closureFormTranslationSchema),
};

export const closureFormSchema = z
  .object(baseFields)
  .superRefine((v, ctx) => {
    if (v.startDate && v.endDate && v.endDate < v.startDate) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'End date must be on or after start date',
        path: ['endDate'],
      });
    }
    // Block half-filled translation rows; fully-empty rows are filtered before submit.
    v.translations.forEach((t, i) => {
      const hasName = t.name.trim().length > 0;
      const hasDescription = t.description.trim().length > 0;
      // Translation is required to have a name if it has a description.
      // Description-only is meaningless without a name.
      if (hasDescription && !hasName) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `Add a ${t.localeName} name or clear the description`,
          path: ['translations', i, 'name'],
        });
      }
    });
  });
export type ClosureFormValues = z.infer<typeof closureFormSchema>;

// ────────────────────────────────────────────────────────────────────────────
// List filters
// ────────────────────────────────────────────────────────────────────────────

// Mirror of ClosureSortKeyValues in the API.
export const CLOSURE_SORT_KEYS = [
  'name',
  'startDate',
  'endDate',
  'updated',
] as const;
export type ClosureSortKey = (typeof CLOSURE_SORT_KEYS)[number];

export type SortDirection = 'asc' | 'desc';

export interface ListClosuresParams {
  page: number;
  pageSize: number;
  searchText?: string;
  isActive?: boolean;
  dateFrom?: string;
  dateTo?: string;
  apmcId?: string;
  sortBy?: ClosureSortKey;
  sortDir?: SortDirection;
}
